"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Droplet, Flame, Gauge, CircleQuestionMark as HelpCircle, OctagonAlert as AlertOctagon, ArrowRight, ShieldAlert, Pipette, Hammer, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface ProblemItem {
  id: string;
  title: string;
  description: string;
  severity: "Critical" | "High" | "Medium" | "Low";
  iconName: string;
  targetHref: string;
  ctaText: string;
}
const problemItems: ProblemItem[] = [{
  id: "leak",
  title: "Burst Pipe & Active Leaks",
  description: "Uncontrolled water spraying, pooling under walls, or flooding living spaces right now.",
  severity: "Critical",
  iconName: "alert",
  targetHref: "#water-leak-problems",
  ctaText: "Stop Active Leaks"
}, {
  id: "toilet",
  title: "Overflowing or Clogged Toilet",
  description: "Toilet bowl backing up, refusing to flush, or leaking dirty water onto bathroom flooring.",
  severity: "Critical",
  iconName: "droplet",
  targetHref: "#toilet-problems",
  ctaText: "Clear Toilet Block"
}, {
  id: "drain",
  title: "Severe Drain Blockages",
  description: "Sinks, showers, or tubs filling with standing water that drains agonizingly slow or not at all.",
  severity: "High",
  iconName: "hammer",
  targetHref: "#drain-problems",
  ctaText: "Unclog Drains"
}, {
  id: "heater",
  title: "Water Heater Failure & Cold Water",
  description: "No hot water, rust-colored streams, rumbling tank noises, or pooling water around the unit base.",
  severity: "High",
  iconName: "flame",
  targetHref: "#water-heater-problems",
  ctaText: "Restore Hot Water"
}, {
  id: "pressure",
  title: "Low Water Pressure",
  description: "Weak flow across multiple faucets or sudden drop in shower volume throughout your home.",
  severity: "Medium",
  iconName: "gauge",
  targetHref: "#water-pressure-problems",
  ctaText: "Fix Pressure Issues"
}, {
  id: "sewer",
  title: "Sewer Backup & Foul Odors",
  description: "Multiple drains gurgling simultaneously, sewage smells in yard, or waste bubbling up indoors.",
  severity: "Critical",
  iconName: "shield",
  targetHref: "#sewer-problems",
  ctaText: "Inspect Main Sewer"
}, {
  id: "pipes",
  title: "Frozen, Cracked or Old Pipes",
  description: "Visible pipe corrosion, noisy rattling pipes in walls, or freeze-risk supply lines during cold snaps.",
  severity: "High",
  iconName: "pipette",
  targetHref: "#pipe-problems",
  ctaText: "Repair Pipe Lines"
}, {
  id: "faucets",
  title: "Leaky Faucets & Running Fixtures",
  description: "Constant dripping sounds, loose handles, fixture mineral buildup, or high unexplained water bills.",
  severity: "Low",
  iconName: "sparkles",
  targetHref: "#faucet-sink-problems",
  ctaText: "Repair Fixtures"
}, {
  id: "emergency",
  title: "Urgent Off-Hours Emergency",
  description: "Major middle-of-the-night plumbing breakdowns requiring immediate dispatch and water shutoff support.",
  severity: "Critical",
  iconName: "alert",
  targetHref: "#emergency-problems",
  ctaText: "Dispatch 24/7 Crew"
}, {
  id: "unknown",
  title: "Not Sure What is Wrong?",
  description: "Hearing running water inside walls, smelling dampness, or dealing with mystery plumbing symptoms.",
  severity: "Medium",
  iconName: "help",
  targetHref: "/contact/",
  ctaText: "Get Diagnostic Help"
}];
function ProblemIcon({
  name
}: {
  name: string;
}) {
  switch (name) {
    case "alert":
      return <AlertOctagon className="size-6 text-destructive" />;
    case "droplet":
      return <Droplet className="size-6 text-primary" />;
    case "hammer":
      return <Hammer className="size-6 text-primary" />;
    case "flame":
      return <Flame className="size-6 text-accent-foreground" />;
    case "gauge":
      return <Gauge className="size-6 text-primary" />;
    case "shield":
      return <ShieldAlert className="size-6 text-destructive" />;
    case "pipette":
      return <Pipette className="size-6 text-primary" />;
    case "sparkles":
      return <Sparkles className="size-6 text-primary" />;
    case "help":
    default:
      return <HelpCircle className="size-6 text-primary" />;
  }
}
function SeverityBadge({
  severity
}: {
  severity: ProblemItem["severity"];
}) {
  if (severity === "Critical") {
    return <Badge variant="destructive" className="font-semibold uppercase tracking-wider text-[10px] px-2 py-0.5">
        Critical Priority
      </Badge>;
  }
  if (severity === "High") {
    return <Badge className="bg-primary text-primary-foreground border-transparent font-semibold uppercase tracking-wider text-[10px] px-2 py-0.5">
        High Severity
      </Badge>;
  }
  if (severity === "Medium") {
    return <Badge variant="secondary" className="font-semibold uppercase tracking-wider text-[10px] px-2 py-0.5">
        Medium Priority
      </Badge>;
  }
  return <Badge variant="outline" className="border-border text-muted-foreground font-semibold uppercase tracking-wider text-[10px] px-2 py-0.5">
      Routine Service
    </Badge>;
}
export default function ProblemFinder() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="problemfindergrid" data-nav="light" className="bg-background py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground mb-4">
            <span>Fast Diagnostic Guide</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl font-default">
            What plumbing problem are you experiencing?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            Select your issue below for immediate guidance, severity triage, and step-by-step local resolution in Jonesboro and surrounding areas.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {problemItems.map((problem, i) => <motion.div key={problem.id} data-index={i} variants={itemVariants} className="flex">
              <Card className="flex flex-col justify-between w-full rounded-2xl border border-border bg-card shadow-xs transition-[box-shadow,border-color] duration-300 hover:border-primary hover:shadow-lg">
                <div>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <div className="flex size-12 items-center justify-center rounded-xl bg-secondary border border-border">
                      <ProblemIcon name={problem.iconName} />
                    </div>
                    <SeverityBadge severity={problem.severity} />
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      {problem.title}
                    </CardTitle>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {problem.description}
                    </CardDescription>
                  </CardContent>
                </div>
                <CardFooter className="pt-4 border-t border-border/60">
                  <Button asChild variant={problem.id === "unknown" ? "default" : "outline"} className="w-full justify-between group font-medium">
                    <Link to={problem.targetHref}>
                      <span>{problem.ctaText}</span>
                      <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}